import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Home, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { PROPERTY_TYPES } from '../lib/docTypes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_FORM = {
  address: '',
  tenant_name: '',
  tenant_email: '',
  tenant_phone: '',
  rent_amount: '',
  rent_due_date: '1',
  tenancy_start_date: '',
  tenancy_end_date: '',
  contract_type: 'AST',
  property_type: 'AST',
};

export default function PropertiesTab({ onUpdate }) {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);

  // AI extraction state (Add Property form)
  const [aiLoading, setAiLoading] = useState(false);
  const [aiReview, setAiReview] = useState(null); // { fields }
  const [astFile, setAstFile] = useState(null); // raw File, attached to the property after creation
  const astFileInputRef = useRef(null);

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/properties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProperties(response.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load properties');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/properties`, {
        ...formData,
        tenant_email: (formData.tenant_email || '').trim() || null,
        tenant_phone: (formData.tenant_phone || '').trim() || null,
        rent_amount: parseFloat(formData.rent_amount),
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const newProperty = response.data;

      // If we used the AI flow, also attach the uploaded AST as a Tenancy
      // Agreement document on the new property so it shows up in the docs tab
      // and there's a clean audit trail.
      if (astFile && newProperty?.id) {
        try {
          const fd = new FormData();
          fd.append('document_type', 'tenancy_agreement');
          fd.append('file', astFile);
          if (formData.tenancy_end_date) {
            fd.append('expiry_date', formData.tenancy_end_date);
          }
          await axios.post(`${API}/properties/${newProperty.id}/documents`, fd, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success('Property added — tenancy agreement saved to documents');
        } catch (docErr) {
          // Don't block property creation if doc upload fails; warn the user.
          toast.warning('Property added, but the tenancy agreement could not be saved to documents.');
        }
      } else {
        toast.success('Property added successfully');
      }

      setDialogOpen(false);
      setFormData(EMPTY_FORM);
      setAstFile(null);
      fetchProperties();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to add property');
    }
  };

  const handleAstUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setAiLoading(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      const response = await axios.post(`${API}/analyze-tenancy-upload`, fd, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const fields = response.data || {};
      const hasAny = Object.values(fields).some(v => v !== null && v !== undefined && v !== '');
      if (hasAny) {
        setAiReview({ fields });
        setAstFile(file); // remember to attach after creation
      } else {
        toast.info('AI could not find any tenancy details in that document.');
        setAstFile(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'AI analysis failed');
      setAstFile(null);
    } finally {
      setAiLoading(false);
    }
  };

  const applyAiReview = () => {
    const f = aiReview?.fields || {};
    setFormData(prev => ({
      ...prev,
      address: f.property_address ?? prev.address,
      tenant_name: f.tenant_name ?? prev.tenant_name,
      tenant_email: f.tenant_email ?? prev.tenant_email,
      tenant_phone: f.tenant_phone ?? prev.tenant_phone,
      rent_amount: f.monthly_rent != null ? String(f.monthly_rent) : prev.rent_amount,
      tenancy_start_date: f.tenancy_start_date ?? prev.tenancy_start_date,
      tenancy_end_date: f.tenancy_end_date ?? prev.tenancy_end_date,
      contract_type: f.contract_type ?? prev.contract_type,
    }));
    setAiReview(null);
    if (f.property_address) {
      toast.success('All details applied — review and save');
    } else {
      toast.success('Details applied — add the address and save');
    }
  };

  const getStatusClass = (status) => {
    if (status === 'urgent') return 'property-card-urgent';
    if (status === 'warning') return 'property-card-warning';
    return 'property-card-normal';
  };

  useEffect(() => {
    if (properties.length > 0 && !selectedPropertyId) {
      setSelectedPropertyId(properties[0].id);
    }
  }, [properties]);

  const renderAddForm = () => (
    <form onSubmit={handleSubmit} className="property-form">
      <div className="tenant-ai-extract">
        <input
          type="file"
          accept="application/pdf,image/png,image/jpeg,image/webp"
          ref={astFileInputRef}
          onChange={handleAstUpload}
          style={{ display: 'none' }}
          data-testid="add-property-ast-input"
        />
        <Button
          type="button"
          onClick={() => astFileInputRef.current?.click()}
          className="btn-tenant-ai"
          disabled={aiLoading}
          data-testid="add-property-ast-button"
        >
          {aiLoading ? (
            <>
              <span className="ai-spinner" />
              Reading document with AI…
            </>
          ) : (
            <>
              <Upload size={16} />
              Extract from Tenancy Agreement
            </>
          )}
        </Button>
        <div className="tenant-ai-hint">
          Or fill in the details manually below.
        </div>
      </div>

      <div className="form-group">
        <Label htmlFor="address">Full Address</Label>
        <Input
          id="address"
          value={formData.address}
          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
          placeholder="123 Main Street, London, SW1A 1AA"
          required
          data-testid="property-address-input"
        />
      </div>

      <div className="form-group">
        <Label htmlFor="tenant_name">Tenant Name</Label>
        <Input
          id="tenant_name"
          value={formData.tenant_name}
          onChange={(e) => setFormData({ ...formData, tenant_name: e.target.value })}
          placeholder="John Smith"
          required
          data-testid="tenant-name-input"
        />
      </div>

      <div className="form-group">
        <Label htmlFor="tenant_email">Tenant Email</Label>
        <Input
          id="tenant_email"
          type="email"
          value={formData.tenant_email}
          onChange={(e) => setFormData({ ...formData, tenant_email: e.target.value })}
          placeholder="tenant@example.com"
          data-testid="tenant-email-input"
        />
      </div>

      <div className="form-group">
        <Label htmlFor="tenant_phone">Tenant Phone</Label>
        <Input
          id="tenant_phone"
          type="tel"
          value={formData.tenant_phone}
          onChange={(e) => setFormData({ ...formData, tenant_phone: e.target.value })}
          placeholder="07123 456789"
          data-testid="tenant-phone-input"
        />
      </div>

      <div className="form-group">
        <Label htmlFor="rent_amount">Monthly Rent (£)</Label>
        <Input
          id="rent_amount"
          type="number"
          step="0.01"
          value={formData.rent_amount}
          onChange={(e) => setFormData({ ...formData, rent_amount: e.target.value })}
          placeholder="1200"
          required
          data-testid="rent-amount-input"
        />
      </div>

      <div className="form-group">
        <Label htmlFor="rent_due_date">Rent Due Date (Day of Month)</Label>
        <Select
          value={formData.rent_due_date}
          onValueChange={(value) => setFormData({ ...formData, rent_due_date: value })}
        >
          <SelectTrigger data-testid="rent-due-date-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[...Array(31)].map((_, i) => (
              <SelectItem key={i + 1} value={String(i + 1)}>
                {i + 1}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="form-group">
        <Label htmlFor="property_type">Property Type</Label>
        <Select
          value={formData.property_type}
          onValueChange={(value) => setFormData({ ...formData, property_type: value })}
        >
          <SelectTrigger data-testid="property-type-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PROPERTY_TYPES.map((p) => (
              <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="form-hint">HMO properties get the extra HMO statutory documents added automatically.</p>
      </div>

      <div className="form-group">
        <Label htmlFor="contract_type">Contract Type</Label>
        <Select
          value={formData.contract_type}
          onValueChange={(value) => setFormData({ ...formData, contract_type: value })}
        >
          <SelectTrigger data-testid="contract-type-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="AST">AST (Assured Shorthold Tenancy)</SelectItem>
            <SelectItem value="Licence">Licence</SelectItem>
            <SelectItem value="Company Let">Company Let</SelectItem>
            <SelectItem value="Other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="form-group">
        <Label htmlFor="tenancy_start_date">Tenancy Start Date</Label>
        <Input
          id="tenancy_start_date"
          type="date"
          value={formData.tenancy_start_date}
          onChange={(e) => setFormData({ ...formData, tenancy_start_date: e.target.value })}
          required
          data-testid="start-date-input"
        />
      </div>

      <div className="form-group">
        <Label htmlFor="tenancy_end_date">Tenancy End Date</Label>
        <Input
          id="tenancy_end_date"
          type="date"
          value={formData.tenancy_end_date}
          onChange={(e) => setFormData({ ...formData, tenancy_end_date: e.target.value })}
          required
          data-testid="end-date-input"
        />
      </div>

      <Button type="submit" className="btn-primary w-full" data-testid="submit-property-button">
        Add Property
      </Button>
    </form>
  );

  const renderAddDialog = () => (
    <Dialog open={dialogOpen} onOpenChange={(open) => {
      setDialogOpen(open);
      if (!open) setAstFile(null);
    }}>
      <DialogTrigger asChild>
        <Button className="btn-primary" data-testid="add-property-button">
          <Plus size={20} />
          Add New Property
        </Button>
      </DialogTrigger>
      <DialogContent className="dialog-content">
        <DialogHeader>
          <DialogTitle>Add New Property</DialogTitle>
          <DialogDescription>
            Add a new rental to your portfolio. Upload a tenancy agreement to let AI auto-fill the form, or enter the details manually.
          </DialogDescription>
        </DialogHeader>
        {renderAddForm()}
      </DialogContent>
    </Dialog>
  );

  const renderAiReviewDialog = () => (
    <Dialog open={!!aiReview} onOpenChange={(open) => {
      if (!open) {
        setAiReview(null);
        // If user closes review without applying, drop the staged file so we
        // don't accidentally attach an unrelated PDF to a manually-entered property.
        setAstFile(null);
      }
    }}>
      <DialogContent className="ai-review-dialog" data-testid="add-property-ai-review-dialog">
        <DialogHeader>
          <DialogTitle>AI extracted these details</DialogTitle>
          <DialogDescription>
            Confirm the property and tenancy details Gemini found in your document before adding the property.
          </DialogDescription>
        </DialogHeader>
        <div className="ai-review-hint">
          Review and adjust anything that looks wrong, then apply to fill the form. Nothing is saved until you click <strong>Add Property</strong>.
        </div>
        <div className="ai-review-grid">
          <div className="form-group ai-review-fullwidth">
            <Label>Full Address</Label>
            <Input
              value={aiReview?.fields?.property_address ?? ''}
              onChange={(e) => setAiReview(r => ({ fields: { ...r.fields, property_address: e.target.value } }))}
              placeholder="If AI couldn't find it, type it here"
              data-testid="add-property-ai-address"
            />
          </div>
          <div className="form-group">
            <Label>Tenant Name</Label>
            <Input
              value={aiReview?.fields?.tenant_name ?? ''}
              onChange={(e) => setAiReview(r => ({ fields: { ...r.fields, tenant_name: e.target.value } }))}
              data-testid="add-property-ai-name"
            />
          </div>
          <div className="form-group">
            <Label>Tenant Email</Label>
            <Input
              type="email"
              value={aiReview?.fields?.tenant_email ?? ''}
              onChange={(e) => setAiReview(r => ({ fields: { ...r.fields, tenant_email: e.target.value || null } }))}
              placeholder="tenant@example.com"
              data-testid="add-property-ai-email"
            />
          </div>
          <div className="form-group">
            <Label>Tenant Phone</Label>
            <Input
              type="tel"
              value={aiReview?.fields?.tenant_phone ?? ''}
              onChange={(e) => setAiReview(r => ({ fields: { ...r.fields, tenant_phone: e.target.value || null } }))}
              placeholder="07123 456789"
              data-testid="add-property-ai-phone"
            />
          </div>
          <div className="form-group">
            <Label>Monthly Rent (£)</Label>
            <Input
              type="number"
              step="0.01"
              value={aiReview?.fields?.monthly_rent ?? ''}
              onChange={(e) => setAiReview(r => ({ fields: { ...r.fields, monthly_rent: e.target.value === '' ? null : parseFloat(e.target.value) } }))}
              data-testid="add-property-ai-rent"
            />
          </div>
          <div className="form-group">
            <Label>Tenancy Start Date</Label>
            <Input
              type="date"
              value={aiReview?.fields?.tenancy_start_date ?? ''}
              onChange={(e) => setAiReview(r => ({ fields: { ...r.fields, tenancy_start_date: e.target.value || null } }))}
              data-testid="add-property-ai-start"
            />
          </div>
          <div className="form-group">
            <Label>Tenancy End Date</Label>
            <Input
              type="date"
              value={aiReview?.fields?.tenancy_end_date ?? ''}
              onChange={(e) => setAiReview(r => ({ fields: { ...r.fields, tenancy_end_date: e.target.value || null } }))}
              data-testid="add-property-ai-end"
            />
          </div>
          <div className="form-group">
            <Label>Contract Type</Label>
            <Select
              value={aiReview?.fields?.contract_type || 'AST'}
              onValueChange={(v) => setAiReview(r => ({ fields: { ...r.fields, contract_type: v } }))}
            >
              <SelectTrigger data-testid="add-property-ai-contract">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="AST">AST</SelectItem>
                <SelectItem value="Licence">Licence</SelectItem>
                <SelectItem value="Company Let">Company Let</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="ai-review-actions">
          <Button onClick={() => { setAiReview(null); setAstFile(null); }} className="btn-secondary" data-testid="add-property-ai-cancel">
            Cancel
          </Button>
          <Button onClick={applyAiReview} className="btn-primary" data-testid="add-property-ai-apply">
            Apply to form
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  if (loading) {
    return <div className="tab-loading">Loading properties...</div>;
  }

  if (properties.length === 0) {
    return (
      <div className="properties-tab">
        <div className="tab-header">{renderAddDialog()}</div>
        <div className="empty-state" data-testid="empty-properties">
          <Home size={64} className="empty-icon" />
          <h3>No properties yet</h3>
          <p>Add your first property to get started</p>
        </div>
        {renderAiReviewDialog()}
      </div>
    );
  }

  return (
    <div className="properties-tab">
      <div className="tab-header">{renderAddDialog()}</div>

      <div className="properties-list">
        {properties.map((property) => (
          <div
            key={property.id}
            className={`property-card-new ${getStatusClass(property.status)}`}
            onClick={() => navigate(`/property/${property.id}`)}
            data-testid={`property-card-${property.id}`}
          >
            <div className="property-card-header">
              <h3 className="property-card-title">{property.address}</h3>
              {property.status !== 'normal' && (
                <span className={`status-indicator status-${property.status}`}>
                  {property.status === 'urgent' ? 'URGENT' : 'WARNING'}
                </span>
              )}
            </div>
            <div className="property-card-body">
              <div className="property-card-row">
                <span className="property-card-label">Tenant:</span>
                <span className="property-card-value">{property.tenant_name}</span>
              </div>
              <div className="property-card-row">
                <span className="property-card-label">Rent:</span>
                <span className="property-card-value">£{property.rent_amount}/month</span>
              </div>
              <div className="property-card-row">
                <span className="property-card-label">Contract:</span>
                <span className="property-card-value">{property.contract_type}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {renderAiReviewDialog()}
    </div>
  );
}
